import { I18nLocale } from "../constants";
import { dayjs } from "@outschool/time";
import { Locale as DateFnsLocale } from "date-fns";
import { Locale } from "dayjs/locale/en";

/** Given a locale string, lazy-load the corresponding date-fns locale */
export async function getDateFnsLocale(
  locale: I18nLocale,
): Promise<DateFnsLocale> {
  switch (locale) {
    case I18nLocale.Ko:
      return (await import("date-fns/locale/ko")).default;
    case I18nLocale.Ja:
      return (await import("date-fns/locale/ja")).default;
    case I18nLocale.ZhTw:
      return (await import("date-fns/locale/zh-TW")).default;
    case I18nLocale.Es:
      return (await import("date-fns/locale/es")).default;
    case I18nLocale.En:
    default:
      return (await import("date-fns/locale/en-US")).default;
  }
}

// "en" is the only locale that should be passed as a string. Passing the
// imported "dayjs/locale/en" locale to dayjs is bugged and will cause errors
export type DayjsLocale = "en" | Locale;
// on webpack chunk splitting to lazy-load the locale files. Chunk splitting does
// not work in lang-js packages.

/** Given a locale string, lazy-load the corresponding dayjs locale */
export async function getDayjsLocale(locale: I18nLocale): Promise<DayjsLocale> {
  switch (locale) {
    case I18nLocale.Ko:
      return await import("dayjs/locale/ko");
    case I18nLocale.Ja:
      return await import("dayjs/locale/ja");
    case I18nLocale.ZhTw:
      return await import("dayjs/locale/zh-tw");
    case I18nLocale.Es:
      return await import("dayjs/locale/es");
    case I18nLocale.En:
    default:
      return await "en";
  }
}

/** Given a locale string, import and set the dayjs locale accordingly */
export async function setDayjsLocale(locale: I18nLocale) {
  const dayjsLocale = await getDayjsLocale(locale);
  dayjs.locale(dayjsLocale);
}

/**
 * Runs a function in an isolated context where the dayjs locale is set to `locale`,
 * regardless of the current global locale. Returns that function's return value.
 * Used for e.g. resolvers so the request locale does not leak to other server code.
 * @param locale A string representing the locale to use
 * @param fn The function to be run in the given locale
 * @returns The return value of `fn`
 */
export async function withDayjsLocale<T extends (...args: any[]) => any>(
  locale: I18nLocale,
  fn: T,
): Promise<ReturnType<T>> {
  const originalLocale = dayjs.locale();
  await setDayjsLocale(locale);
  let result, error;
  try {
    result = await fn();
  } catch (e) {
    error = e;
  } finally {
    await setDayjsLocale(originalLocale as I18nLocale);
  }
  if (error) {
    throw error;
  }
  return result;
}
